import { isAwsError, isNetworkError } from '@allurion/utils';
import { Component, PropsWithChildren } from 'react';

import { Logger } from 'src/services/Logger';

import { ErrorBoundaryMessage } from './ErrorBoundaryMessage';

type errorState = {
  hasError: boolean;
  kind?: 'forbidden' | 'notFound' | 'unknown' | 'unauthorized';
};

export class ErrorBoundary extends Component<PropsWithChildren<unknown>, errorState> {
  constructor(props: any) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError = (error: any) => ({
    hasError: true,
    kind: this.getErrorKind(error),
  });

  private static getErrorKind(error: any) {
    if (error.message === 'Forbidden') {
      return 'forbidden';
    }

    if (
      error.message === 'Unauthorized' ||
      (isAwsError(error) && error.code === 'NotAuthorizedException') ||
      error.message.includes('Refresh Token has expired')
    ) {
      return 'unauthorized';
    }

    return 'unknown';
  }

  componentDidCatch(error: any) {
    const { kind } = this.state;

    switch (kind) {
      case 'forbidden':
        //ignore
        break;

      case 'unauthorized':
        //ignore
        break;

      default:
        // A request that never completed reaches the boundary as a bare
        // `TypeError` from `Api.httpRequest`'s `fetch`, with no status to
        // classify it by, so `getErrorKind` can only call it `unknown` -- and
        // every one of those was reported as an application error.
        // INSIGHTS-UI-J4 collects them: 2,320 events since 2024-03. That issue
        // groups on the `Api.httpRequest` frame, which the hand-rolled
        // `.catch`es reporting the same rejection share (`usePatientTreatments`,
        // `useProviderAccess`), so this call site is a share of it rather than
        // the whole of it -- the rest keep reporting at error level.
        //
        // Demoted, not dropped: `getErrorKind` is deliberately left alone, so
        // the screen the user sees is unchanged, and the event still arrives
        // -- a CORS or CSP regression rejects `fetch` identically and must
        // stay visible. See `isNetworkError`.
        Logger.captureException(
          error,
          isNetworkError(error)
            ? { level: 'warning', fingerprint: ['request-transport-failed'] }
            : undefined
        );
        break;
    }
  }

  render() {
    const { children } = this.props;
    const { hasError, kind } = this.state;

    return hasError ? <ErrorBoundaryMessage kind={kind} /> : children;
  }
}
